// Client-side full-text search over the bundled Help docs. The corpus is small
// (~two dozen markdown files) so a linear scan with a per-doc token set is more
// than fast enough — no external index library, no build step.

import type { HelpDoc } from './docs';

export interface SearchHit {
  doc: HelpDoc;
  /** Total match weight — title matches score higher than body matches. */
  score: number;
  /** A short body excerpt around the first match, for the results list. */
  snippet: string;
  /** The query terms that matched, for highlighting. */
  matchedTerms: string[];
}

// Strip markdown syntax to plain text so snippets and matching don't trip over
// `#`, `*`, backticks, link brackets, table pipes, etc.
export function stripMarkdown(md: string): string {
  return md
    .replace(/```[\s\S]*?```/g, ' ') // fenced code blocks
    .replace(/`([^`]+)`/g, '$1') // inline code
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ') // images
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1') // links → link text
    .replace(/^#{1,6}\s+/gm, '') // heading markers
    .replace(/^>\s?/gm, '') // blockquote markers
    .replace(/^\s*[-*+]\s+/gm, '') // list bullets
    .replace(/^\s*\d+\.\s+/gm, '') // ordered-list markers
    .replace(/[*_~]/g, '') // emphasis
    .replace(/\|/g, ' ') // table pipes
    .replace(/\s+/g, ' ')
    .trim();
}

function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length > 1);
}

interface IndexEntry {
  doc: HelpDoc;
  plain: string;
  plainLower: string;
  titleLower: string;
  tokens: Set<string>;
}

export interface HelpSearchIndex {
  search(query: string): SearchHit[];
}

export function buildSearchIndex(docs: HelpDoc[]): HelpSearchIndex {
  const entries: IndexEntry[] = docs.map((doc) => {
    const plain = stripMarkdown(doc.body);
    return {
      doc,
      plain,
      plainLower: plain.toLowerCase(),
      titleLower: doc.title.toLowerCase(),
      tokens: new Set(tokenize(`${doc.title} ${plain}`)),
    };
  });

  function makeSnippet(plain: string, plainLower: string, terms: string[]): string {
    let idx = -1;
    for (const term of terms) {
      const found = plainLower.indexOf(term);
      if (found !== -1 && (idx === -1 || found < idx)) idx = found;
    }
    if (idx === -1) return plain.slice(0, 160).trim() + (plain.length > 160 ? '…' : '');
    const start = Math.max(0, idx - 60);
    const end = Math.min(plain.length, idx + 100);
    return (start > 0 ? '…' : '') + plain.slice(start, end).trim() + (end < plain.length ? '…' : '');
  }

  return {
    search(query: string): SearchHit[] {
      const terms = tokenize(query);
      if (terms.length === 0) return [];
      const hits: SearchHit[] = [];
      for (const entry of entries) {
        let score = 0;
        const matched: string[] = [];
        for (const term of terms) {
          // Prefix match against indexed tokens so "onboard" hits "onboarding".
          let tokenMatch = false;
          for (const tok of entry.tokens) {
            if (tok === term || tok.startsWith(term)) {
              tokenMatch = true;
              break;
            }
          }
          if (!tokenMatch) continue;
          matched.push(term);
          if (entry.titleLower.includes(term)) score += 10;
          // Count body occurrences (capped) so denser matches rank higher.
          let count = 0;
          let from = entry.plainLower.indexOf(term);
          while (from !== -1 && count < 5) {
            count++;
            from = entry.plainLower.indexOf(term, from + term.length);
          }
          score += count;
        }
        // Require every query term to match somewhere (AND semantics).
        if (matched.length === terms.length && score > 0) {
          hits.push({
            doc: entry.doc,
            score,
            snippet: makeSnippet(entry.plain, entry.plainLower, terms),
            matchedTerms: matched,
          });
        }
      }
      return hits.sort((a, b) => b.score - a.score || a.doc.order - b.doc.order);
    },
  };
}
